
import React from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Legend, BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';
import { Activity, Zap, AlertTriangle, Clock } from 'lucide-react';
import KpiDetailLayout from '@/components/kpi/KpiDetailLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

// Sample data for energy consumption
const hourlyData = [
  { hour: '00:00', consumption: 42, average: 38, isAnomaly: false },
  { hour: '01:00', consumption: 38, average: 35, isAnomaly: false },
  { hour: '02:00', consumption: 35, average: 32, isAnomaly: false },
  { hour: '03:00', consumption: 30, average: 30, isAnomaly: false },
  { hour: '04:00', consumption: 32, average: 31, isAnomaly: false },
  { hour: '05:00', consumption: 35, average: 34, isAnomaly: false },
  { hour: '06:00', consumption: 45, average: 40, isAnomaly: false },
  { hour: '07:00', consumption: 62, average: 55, isAnomaly: false },
  { hour: '08:00', consumption: 78, average: 70, isAnomaly: false },
  { hour: '09:00', consumption: 85, average: 75, isAnomaly: false },
  { hour: '10:00', consumption: 88, average: 78, isAnomaly: false },
  { hour: '11:00', consumption: 90, average: 80, isAnomaly: true },
  { hour: '12:00', consumption: 95, average: 82, isAnomaly: false },
  { hour: '13:00', consumption: 92, average: 84, isAnomaly: false },
  { hour: '14:00', consumption: 88, average: 83, isAnomaly: false },
  { hour: '15:00', consumption: 85, average: 82, isAnomaly: false },
  { hour: '16:00', consumption: 82, average: 80, isAnomaly: false },
  { hour: '17:00', consumption: 78, average: 75, isAnomaly: false },
  { hour: '18:00', consumption: 75, average: 72, isAnomaly: false },
  { hour: '19:00', consumption: 70, average: 68, isAnomaly: false },
  { hour: '20:00', consumption: 68, average: 65, isAnomaly: false },
  { hour: '21:00', consumption: 65, average: 60, isAnomaly: false },
  { hour: '22:00', consumption: 58, average: 55, isAnomaly: false },
  { hour: '23:00', consumption: 52, average: 45, isAnomaly: false },
];

const dailyData = [
  { day: 'Mon', consumption: 580, peak: 95 },
  { day: 'Tue', consumption: 560, peak: 92 },
  { day: 'Wed', consumption: 590, peak: 96 },
  { day: 'Thu', consumption: 620, peak: 98 },
  { day: 'Fri', consumption: 600, peak: 97 },
  { day: 'Sat', consumption: 480, peak: 85 },
  { day: 'Sun', consumption: 450, peak: 80 },
];

const facilityData = [
  { name: 'HVAC', value: 45 },
  { name: 'Lighting', value: 20 },
  { name: 'Equipment', value: 25 },
  { name: 'Other', value: 10 },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

// Custom dot component for recharts Line
const CustomizedDot = (props) => {
  const { cx, cy, payload } = props;
  
  if (payload.isAnomaly) {
    return (
      <svg x={cx - 6} y={cy - 6} width={12} height={12} fill="red" viewBox="0 0 12 12">
        <circle cx="6" cy="6" r="6" fill="#FF0000" stroke="#FF0000" />
      </svg>
    );
  }
  
  return (
    <svg x={cx - 3} y={cy - 3} width={6} height={6} fill="blue" viewBox="0 0 6 6">
      <circle cx="3" cy="3" r="3" />
    </svg>
  );
};

// Overview tab content
const OverviewTab = () => (
  <div className="p-4 space-y-6">
    <div>
      <h3 className="text-lg font-medium mb-3">Daily Energy Consumption</h3>
      <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-800">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={hourlyData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="hour" 
              tick={{ fill: '#888', fontSize: 12 }}
              interval="preserveStartEnd"
            />
            <YAxis 
              tick={{ fill: '#888', fontSize: 12 }}
              label={{ value: 'kWh', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="consumption"
              stroke="#2563EB"
              strokeWidth={2}
              dot={<CustomizedDot />}
              activeDot={{ r: 6 }}
              name="Actual Consumption"
            />
            <Line
              type="monotone"
              dataKey="average"
              stroke="#94A3B8"
              strokeDasharray="5 5"
              strokeWidth={2}
              dot={false}
              name="Historical Average"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-sm text-gray-500 mt-2">
        Hourly energy consumption with historical average comparison. Red markers indicate anomalies.
      </p>
    </div>
    
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardContent className="p-4">
          <h3 className="text-lg font-medium mb-3">Weekly Consumption</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="consumption" name="Total kWh" fill="#3B82F6" />
              <Bar dataKey="peak" name="Peak kW" fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-4">
          <h3 className="text-lg font-medium mb-3">Energy Distribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={facilityData}
                cx="50%"
                cy="50%"
                labelLine={true}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                nameKey="name"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {facilityData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  </div>
);

// Trends tab content
const TrendsTab = () => (
  <div className="p-4 space-y-6">
    <h3 className="text-lg font-medium mb-3">Monthly Consumption Trend</h3>
    <p className="text-gray-600 dark:text-gray-400 mb-4">
      Monthly energy consumption data showing year-over-year comparison with trends and patterns.
    </p>
    <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
      <p className="text-gray-500">Monthly trend chart would appear here</p>
    </div>
  </div>
);

// Anomalies tab content
const AnomaliesTab = () => (
  <div className="p-4 space-y-6">
    <div className="flex items-center gap-2 mb-4">
      <AlertTriangle className="text-amber-500 h-5 w-5" />
      <h3 className="text-lg font-medium">Detected Anomalies</h3>
    </div>
    
    <Card>
      <CardContent className="p-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-3">
            <div>
              <p className="font-medium">Unusual consumption spike</p>
              <p className="text-sm text-gray-500">11:00 AM - 12:00 PM</p>
            </div>
            <span className="px-2 py-1 bg-red-100 text-red-800 rounded-md text-sm">+15% above normal</span>
          </div>
          
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-3">
            <div>
              <p className="font-medium">Extended baseload period</p>
              <p className="text-sm text-gray-500">Yesterday, 1:00 AM - 4:30 AM</p>
            </div>
            <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded-md text-sm">+8% above normal</span>
          </div>
          
          <div className="flex items-center justify-between pb-3">
            <div>
              <p className="font-medium">Off-hours consumption</p>
              <p className="text-sm text-gray-500">Last weekend</p>
            </div>
            <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded-md text-sm">+10% above normal</span>
          </div>
        </div>
      </CardContent>
    </Card>
    
    <p className="text-sm text-gray-500">
      Anomaly detection based on machine learning algorithms that identify unusual patterns in energy consumption.
    </p>
  </div>
);

// Main Energy Consumption component
const EnergyConsumption = () => {
  const tabs = [
    { id: 'overview', label: 'Overview', content: <OverviewTab /> },
    { id: 'trends', label: 'Trends & Patterns', content: <TrendsTab /> },
    { id: 'anomalies', label: 'Anomalies', content: <AnomaliesTab /> }
  ];
  
  return (
    <KpiDetailLayout
      id="energy-consumption"
      title="Energy Consumption"
      icon={<Zap className="h-5 w-5 text-blue-600" />}
      colorClass="bg-blue-50 dark:bg-blue-900/20"
      tabs={tabs}
    />
  );
};

export default EnergyConsumption;
